import { useEffect, useState } from "react";
import { ImageCarousel } from "../components/pages/home/imageCaraousel";
import ContentHeader from "../components/common/content/content-header";
import {
  StyledContainer,
  StyledStack,
} from "../components/common/content/styles";
import { Typography } from "@mui/material";
import YouTubeIcon from "@mui/icons-material/YouTube";
import { Colors } from "../utilities/styles";
import { ActionLink } from "./styles";
import { PortableText } from "@portabletext/react";
import { portableTextComponents } from "../sanity/portableText";
import { fallbackHomePage, fallbackLatestNews } from "../sanity/fallbacks";
import { getCachedHomePage, getContentItemsByCategory, getHomePage } from "../sanity/queries";
import { ContentItem, HomePage } from "../sanity/types";
import { urlFor } from "../sanity/image";
import GeneralContentCard, {
  GeneralContentCardSkeleton,
} from "../components/common/content/general-content-card";
import { Seo } from "../seo/Seo";
import {AlertToast} from "../components/common/alert-toast";

export default function Home() {
  const liveStreamCta = {
    heading: "Live Stream",
    message: "To see our Live Stream, please visit our YouTube page",
    linkLabel: "Here",
    linkUrl: "https://www.youtube.com/@stgeorgecocchicagoil506/streams",
  };

  const sermonsCta = {
    heading: "Sermons",
    message: "To see our sermons, please visit our YouTube page",
    linkLabel: "Here",
    linkUrl: "https://www.youtube.com/channel/UChEJfGq4Vynw3vxxyyTXshg",
  };

  const cachedHome = getCachedHomePage();
  const [homePage, setHomePage] = useState<HomePage>(
    cachedHome || fallbackHomePage
  );
  const [homeLoading, setHomeLoading] = useState(!cachedHome);
  const [latestNews, setLatestNews] = useState<ContentItem[]>([]);
  const [newsLoading, setNewsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const heroImage = homePage?.carouselSlides?.[0];
  const seoImage = heroImage?.image
    ? urlFor(heroImage.image).width(1600).url()
    : heroImage?.imageUrl
    ? process.env.PUBLIC_URL + heroImage.imageUrl
    : undefined;

  useEffect(() => {
    let isMounted = true;
    const fetchWithRetry = async () => {
      let attempts = 0;
      while (attempts < 2 && isMounted) {
        try {
          const data = await getHomePage();
          if (!isMounted) return;
          if (!data) {
            setHomePage(fallbackHomePage);
          } else {
            const merged: HomePage = {
              ...fallbackHomePage,
              ...data,
              carouselSlides:
                data.carouselSlides && data.carouselSlides.length
                  ? data.carouselSlides
                  : fallbackHomePage.carouselSlides,
              highlights: data.highlights || [],
            };
            setHomePage(merged);
          }
          setHomeLoading(false);
          return;
        } catch {
          attempts += 1;
          if (attempts >= 2 && isMounted) {
            setHomePage(fallbackHomePage);
            setHomeLoading(false);
          }
        }
      }
    };
    if (homeLoading) {
      fetchWithRetry();
    } else {
      // still refresh in background to update cache/content
      fetchWithRetry();
    }
    return () => {
      isMounted = false;
    };
  }, [homeLoading]);

  useEffect(() => {
    let isMounted = true;
    getContentItemsByCategory("latestNews")
      .then((items: ContentItem[]) => {
        if (!isMounted) {
          return;
        }
        setLatestNews(items.length ? items : fallbackLatestNews);
      })
      .catch(() => {
        if (isMounted) {
          setLatestNews(fallbackLatestNews);
          setErrorMessage("Unable to load latest news. Showing fallback content.");
        }
      })
      .finally(() => {
        if (isMounted) setNewsLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const renderCtaSection = (section: {
    heading: string;
    message: string;
    linkLabel: string;
    linkUrl: string;
  }) => {
    const heading = section.heading;
    if (!heading) {
      return null;
    }
    const message = section.message;
    const linkLabel = section.linkLabel || "Here";
    const linkUrl = section.linkUrl;

    return (
      <>
        <ContentHeader title={heading} />
        <Typography
          color="text.primary"
          variant="body1"
          fontWeight="bold"
          textAlign="center"
          marginBottom="25px"
        >
          {message}
          {linkUrl && (
            <>
              {" "}
              <ActionLink
                aria-label="Youtube Link"
                sx={{ "&:hover": { color: Colors.RedYoutube } }}
                href={linkUrl}
                rel="noopener"
                target="_blank"
              >
                {linkLabel} <YouTubeIcon />
              </ActionLink>
            </>
          )}
        </Typography>
      </>
    );
  };

  return (
    <>
      <Seo
        title={homePage?.metaTitle || homePage?.title || "Home"}
        description={homePage?.metaDescription}
        path="/"
        image={seoImage}
      />
      <ImageCarousel slides={homePage?.carouselSlides || []} />
      <StyledContainer maxWidth={false}>
        <AlertToast
          open={!!errorMessage}
          message={errorMessage}
          onClose={() => setErrorMessage("")}
          severity="warning"
        />
        {renderCtaSection(liveStreamCta)}

        {homePage?.highlights?.map((highlight, index) => {
            const highlightImageUrl = highlight.image
              ? urlFor(highlight.image).width(1600).url()
              : highlight.imageUrl
              ? process.env.PUBLIC_URL + highlight.imageUrl
              : "";
            return (
              <div key={highlight._key || index}>
                {highlight.title && <ContentHeader title={highlight.title} />}
                {highlight.body && (
                  <PortableText
                    value={highlight.body}
                    components={portableTextComponents}
                  />
                )}
                {highlightImageUrl && (
                  <Typography
                    color="text.primary"
                    variant="body1"
                    fontWeight="bold"
                    textAlign="center"
                    marginBottom="25px"
                  >
                    <img
                      width="100%"
                      src={highlightImageUrl}
                      loading="lazy"
                      alt={
                        highlight.imageAlt ||
                        highlight.image?.alt ||
                        highlight.title ||
                        "Highlight"
                      }
                    />
                  </Typography>
                )}
              </div>
            );
          })}

        {(newsLoading || latestNews.length > 0) && (
          <>
            <ContentHeader title="Latest News" />
            <StyledStack>
              {newsLoading
                ? [0, 1, 2].map((i) => (
                    <GeneralContentCardSkeleton key={`news-skeleton-${i}`} />
                  ))
                : latestNews.map((item, index) => (
                    <GeneralContentCard
                      key={item._id || item.slug || index}
                      content={item}
                    />
                  ))}
            </StyledStack>
          </>
        )}

        {renderCtaSection(sermonsCta)}
      </StyledContainer>
    </>
  );
}
